/**
 * Runtime configuration, read from the `window.__env__` block that
 * `scripts/generate-env-config.js` writes and `index.html` loads before the
 * bundle. Read it through these helpers rather than touching `window` — every
 * call site then gets the same normalisation, and a test can set `__env__`
 * without knowing how each consumer parses it.
 */

export interface RuntimeConfig {
  turnstileUrl: string;
  arcadeUrl: string;
}

declare global {
  interface Window {
    __env__?: Partial<RuntimeConfig>;
  }
}

export class RuntimeConfigMissingError extends Error {
  constructor(key: keyof RuntimeConfig) {
    super(
      `Runtime config "${key}" is not set. Run "npm run generate-env-config" ` +
        `(or set the matching env var in the deploy workflow).`,
    );
    this.name = 'RuntimeConfigMissingError';
  }
}

function read(key: keyof RuntimeConfig): string {
  const value = window.__env__?.[key];
  if (!value) {
    throw new RuntimeConfigMissingError(key);
  }
  return value.replace(/\/+$/, '');
}

/** Turnstile origin — where the OTP challenge and session endpoints live. */
export function turnstileUrl(): string {
  return read('turnstileUrl');
}

/**
 * Arcade's GraphQL endpoint. The config value may be either the service
 * origin or the full endpoint, so `/graphql` is appended only when absent —
 * the deploy workflows pass an origin, a local override often pastes the URL
 * straight out of a browser tab.
 */
export function arcadeGraphqlEndpoint(): string {
  return `${arcadeOrigin()}/graphql`;
}

/** Arcade's origin, for the REST API that API keys call. */
export function arcadeOrigin(): string {
  return read('arcadeUrl').replace(/\/graphql$/, '');
}

/**
 * A label for the deployment this build talks to, read off the Arcade host:
 * `local`, `staging`, or null for production, which needs no badge.
 */
export function environmentName(): string | null {
  let host: string;
  try {
    host = new URL(read('arcadeUrl')).hostname;
  } catch {
    return null;
  }
  if (host === 'localhost' || host === '127.0.0.1') {
    return 'local';
  }
  if (/(^|[.-])(stg|staging)([.-]|$)/.test(host)) {
    return 'staging';
  }
  if (/(^|[.-])pr-\d+([.-]|$)/.test(host)) {
    return 'preview';
  }
  return null;
}
